package org.ttt.autogenesis.fleetcontrol.client

/** Prefix identifying Fleet Control's gzip-compressed bearer representation. */
const val COMPRESSED_ACCESS_TOKEN_PREFIX: String = "ab-gzip1."

/** Maximum encoded compressed payload accepted by the Fleet Control authorizer. */
const val MAX_COMPRESSED_ACCESS_TOKEN_LENGTH: Int = 8_192

/** Maximum uncompressed bearer payload kept below API Gateway's header limit. */
const val MAX_UNCOMPRESSED_ACCESS_TOKEN_LENGTH: Int = 8_192

/** Maximum UTF-8 token size accepted after decompression. */
const val MAX_DECOMPRESSED_ACCESS_TOKEN_LENGTH: Int = 65_536

/**
 * Builds the Authorization header value used by Fleet Control clients.
 *
 * Compression is deliberately an application-level representation inside the
 * bearer value. API Gateway still sees a normal `Authorization` header, while
 * the Fleet Control authorizer expands the value before AccelByte IAM
 * introspection. Short tokens remain uncompressed for compatibility.
 *
 * @param accessToken Raw AccelByte access token without the bearer scheme.
 * @return A `Bearer` header value, compressed when that reduces its size.
 */
suspend fun fleetControlAuthorizationHeaderValue(accessToken: String): String
{
    val token = accessToken.trim().takeIf { it.isNotEmpty() }
        ?: throw FleetControlAuthenticationException()
    val compressed = compressAccessToken(token)
    val compressedPayload = compressed?.takeIf {
        it.length + COMPRESSED_ACCESS_TOKEN_PREFIX.length < token.length &&
            it.length <= MAX_COMPRESSED_ACCESS_TOKEN_LENGTH
    }
    val payload = compressedPayload?.let { COMPRESSED_ACCESS_TOKEN_PREFIX + it } ?: token
    if(payload.length > MAX_UNCOMPRESSED_ACCESS_TOKEN_LENGTH)
    {
        throw FleetControlTokenTooLargeException()
    }
    return "Bearer $payload"
}

/** Reports that a token could not fit the Fleet Control authorization header. */
class FleetControlTokenTooLargeException : IllegalStateException(
    "Fleet Control bearer token exceeded the authorization header size limit"
)

internal expect suspend fun compressAccessToken(accessToken: String): String?

